import { router } from "@inertiajs/react"
import { Ellipsis, Globe, Phone, PhoneOutgoing } from "lucide-react"
import { useState } from "react"

import { DEFAULT_LEAD_RESPONDER_DATA } from "@/components/agents/dashboard/lead-responder/edit-sheet"
import {
  AppearancePanel as ChatAppearancePanel,
  AppointmentsPanel as ChatAppointmentsPanel,
  BehaviorPanel as ChatBehaviorPanel,
  CustomizationPanel as ChatCustomizationPanel,
  MessagesPanel as ChatMessagesPanel,
  SettingsPanel as ChatInstallPanel,
  initFields as initChatFields,
} from "@/components/agents/edit/chatbot/edit-sheet"
import {
  type EditableFields as ReceptionistFields,
  readFields as readReceptionistFields,
} from "@/components/agents/edit/receptionist/edit-sheet"
import { AppointmentsPanel } from "@/components/agents/edit/receptionist/panels/appointments-panel"
import { GeneralPanel } from "@/components/agents/edit/receptionist/panels/general-panel"
import { MessagesPanel } from "@/components/agents/edit/receptionist/panels/messages-panel"
import { TransfersPanel } from "@/components/agents/edit/receptionist/panels/transfers-panel"
import type { CalendarEventType } from "@/components/agents/setup/chatbot/conversions-step"
import type { ChatbotSetupData } from "@/components/agents/setup/chatbot/types"
import {
  CUSTOM_INSTRUCTIONS_PLACEHOLDERS,
  CustomInstructionsField,
} from "@/components/agents/setup/custom-instructions"
import {
  CadenceFields,
  type CalendarConnect,
  HandoffFields,
} from "@/components/agents/setup/lead-responder/fields"
import {
  type CrmSource,
  type LeadIntake,
  SourcesFields,
} from "@/components/agents/setup/lead-responder/sources-step"
import type { LeadResponderSetupData } from "@/components/agents/setup/lead-responder/types"
import { LiveTestCall } from "@/components/agents/setup/live-test-call"
import { formatPhoneDisplay } from "@/components/agents/setup/receptionist/phone-utils"
import type {
  CalComEventType,
  CalendlyEventType,
  ConnectedCalendars,
  TransferPreset,
} from "@/components/agents/setup/types"
import type { DayHours } from "@/components/business-profile/hours-editor"
import { CopyButton } from "@/components/copy-button"
import { DrawerHeader, DrawerSheet } from "@/components/drawer-sheet"
import { ChatTestPanel } from "@/components/jobs/chat-test-panel"
import { ForwardingView } from "@/components/jobs/forwarding-view"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Label } from "@/components/ui/label"
import { SheetFooter } from "@/components/ui/sheet"
import {
  Tabs,
  TabsBar,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import {
  agentPath,
  cancelAgentPath,
  pauseAgentPath,
  resumeAgentPath,
} from "@/routes"

// IMP-523: the Manage drawer — each job's mechanics (Settings) and
// capabilities (Capabilities), wired to the same per-agent setup_data the
// retired dashboards edited. Capabilities are stored and edited per job with
// no cross-job sharing: divergence (different transfer targets, multiple
// calendars) is plausible, and merging values up later is cheap while
// splitting a shared value apart is not.

export interface ManagedJob {
  slug: "receptionist" | "website-chat" | "lead-responder"
  name: string
  agent: {
    uid: string
    name: string
    state: string
    setup_data: Record<string, unknown>
  }
  paused: boolean
  identifier: string | null
  scheduled_cancellation: unknown
  // receptionist
  phone_number?: string | null
  // The server sends one shape for both consumers; the receptionist and chat
  // panels type it differently, so each body casts to its panel's view.
  connected_calendars?: Record<string, boolean>
  calendly_event_types?: unknown[]
  cal_com_event_types?: unknown[]
  transfer_presets?: TransferPreset[]
  transfer_hours_prefill?: DayHours[]
  // lead responder
  lead_intake?: LeadIntake | null
  crm?: CrmSource
  sms_registration?: { status: string }
  texting_enabled?: boolean
}

const IDENTIFIER_ICON = {
  receptionist: Phone,
  "website-chat": Globe,
  "lead-responder": PhoneOutgoing,
} as const

function PausedBadge() {
  return (
    <span className="inline-flex shrink-0 items-center rounded-md border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-500">
      Paused
    </span>
  )
}

// The overflow menu: Pause/Resume, and Cancel behind a confirm.
function JobActions({ job }: { job: ManagedJob }) {
  const [confirmingCancel, setConfirmingCancel] = useState(false)

  function toggle() {
    const path = job.paused ? resumeAgentPath : pauseAgentPath
    router.post(path({ id: job.agent.uid }), {}, { preserveScroll: true })
  }

  function cancel() {
    router.post(
      cancelAgentPath({ id: job.agent.uid }),
      {},
      { preserveScroll: true },
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 cursor-pointer rounded-md"
            aria-label="Job actions"
          >
            <Ellipsis className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={toggle}>
            {job.paused ? "Resume" : "Pause"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onClick={() => setConfirmingCancel(true)}
          >
            Cancel job
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmingCancel} onOpenChange={setConfirmingCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel {job.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The job stops working for you. If it&rsquo;s billed, it stays
              active until the end of the current billing period.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={cancel}>Cancel job</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function saveSetupData(
  agentUid: string,
  data: Record<string, unknown>,
  opts: {
    onFinish: () => void
    onSuccess: () => void
    onError?: (errors: Record<string, string>) => void
  },
) {
  router.patch(
    agentPath({ id: agentUid }),
    { setup_data: data } as unknown as Record<string, string>,
    {
      preserveScroll: true,
      onFinish: opts.onFinish,
      onSuccess: opts.onSuccess,
      onError: (errors) => opts.onError?.(errors as Record<string, string>),
    },
  )
}

const TAB_CONTENT_CLASS = "flex-1 space-y-6 overflow-y-auto px-6 py-5"

function SaveFooter({
  processing,
  disabled,
  onSave,
}: {
  processing: boolean
  disabled?: boolean
  onSave: () => void
}) {
  return (
    <SheetFooter className="border-t px-6 py-4">
      <Button
        className="w-full rounded-md"
        onClick={onSave}
        disabled={processing || disabled}
      >
        {processing ? "Saving…" : "Save changes"}
      </Button>
    </SheetFooter>
  )
}

// ── Receptionist ─────────────────────────────────────────────────────────────

function ReceptionistBody({
  job,
  onClose,
  onForwarding,
}: {
  job: ManagedJob
  onClose: () => void
  onForwarding: () => void
}) {
  const [fields, setFields] = useState<ReceptionistFields>(() =>
    readReceptionistFields(job.agent, job.transfer_hours_prefill ?? []),
  )
  const [processing, setProcessing] = useState(false)
  const [eventTypeError, setEventTypeError] = useState<string | null>(null)

  function update<K extends keyof ReceptionistFields>(
    key: K,
    value: ReceptionistFields[K],
  ) {
    if (
      key === "appointment_calendar_kind" ||
      key === "appointment_event_type_uri"
    ) {
      setEventTypeError(null)
    }
    setFields((prev) => ({ ...prev, [key]: value }))
  }

  function save() {
    setProcessing(true)
    saveSetupData(
      job.agent.uid,
      {
        ...fields,
        handoff_custom_reasons: fields.handoff_custom_reasons
          .map((r) => r.trim())
          .filter((r) => r.length > 0),
      },
      {
        onFinish: () => setProcessing(false),
        onSuccess: onClose,
        onError: (errors) =>
          setEventTypeError(errors.appointment_event_type_uri ?? null),
      },
    )
  }

  const phone = formatPhoneDisplay(job.phone_number ?? null)

  return (
    <>
      <Tabs
        defaultValue="settings"
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        <TabsBar>
          <TabsList variant="line">
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
            <TabsTrigger value="test">Test</TabsTrigger>
          </TabsList>
        </TabsBar>

        <TabsContent value="settings" className={TAB_CONTENT_CLASS}>
          <GeneralPanel fields={fields} onChange={update} />

          <section className="space-y-2.5">
            <div>
              <Label>Phone number</Label>
              <p className="text-muted-foreground mt-1 text-sm">
                Forward your business line here and the assistant picks up.
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="text-sm font-medium">
                  {phone || "Being provisioned"}
                </span>
                {phone && (
                  <CopyButton value={phone} label="Copy phone number" />
                )}
              </span>
              {phone && (
                <Button variant="outline" size="sm" onClick={onForwarding}>
                  Set up call forwarding
                </Button>
              )}
            </div>
          </section>

          <CustomInstructionsField
            value={fields.custom_instructions}
            placeholder={CUSTOM_INSTRUCTIONS_PLACEHOLDERS.receptionist}
            onChange={(value) => update("custom_instructions", value)}
          />
        </TabsContent>

        <TabsContent value="capabilities" className={TAB_CONTENT_CLASS}>
          <MessagesPanel fields={fields} onChange={update} />
          <AppointmentsPanel
            fields={fields}
            onChange={update}
            connectedCalendars={
              (job.connected_calendars ?? {}) as ConnectedCalendars
            }
            calendlyEventTypes={
              (job.calendly_event_types ?? []) as CalendlyEventType[]
            }
            calComEventTypes={
              (job.cal_com_event_types ?? []) as CalComEventType[]
            }
            agentUid={job.agent.uid}
            eventTypeError={eventTypeError}
          />
          {/* Transfer calls is a voice capability — it appears only here. */}
          <TransfersPanel
            fields={fields}
            onChange={update}
            transferPresets={job.transfer_presets ?? []}
          />
        </TabsContent>

        <TabsContent value="test" className={TAB_CONTENT_CLASS}>
          <LiveTestCall
            agentUid={job.agent.uid}
            idleDescription="Use your browser microphone to talk to me."
            speakerLabel="Assistant"
            waitingMessage="Waiting for the conversation to start…"
          />
        </TabsContent>
      </Tabs>

      <SaveFooter
        processing={processing}
        disabled={!!eventTypeError}
        onSave={save}
      />
    </>
  )
}

// ── Website chat ─────────────────────────────────────────────────────────────

function WebsiteChatBody({
  job,
  onClose,
}: {
  job: ManagedJob
  onClose: () => void
}) {
  const [fields, setFields] = useState<ChatbotSetupData>(() =>
    initChatFields(job.agent.setup_data as Partial<ChatbotSetupData>),
  )
  const [processing, setProcessing] = useState(false)

  function update<K extends keyof ChatbotSetupData>(
    key: K,
    value: ChatbotSetupData[K],
  ) {
    setFields((prev) => ({ ...prev, [key]: value }))
  }

  function save() {
    setProcessing(true)
    saveSetupData(job.agent.uid, fields as unknown as Record<string, unknown>, {
      onFinish: () => setProcessing(false),
      onSuccess: onClose,
    })
  }

  return (
    <>
      <Tabs
        defaultValue="settings"
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        <TabsBar>
          <TabsList variant="line">
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
            <TabsTrigger value="test">Test</TabsTrigger>
          </TabsList>
        </TabsBar>

        <TabsContent value="settings" className={TAB_CONTENT_CLASS}>
          <ChatBehaviorPanel fields={fields} onChange={update} />
          <ChatAppearancePanel fields={fields} onChange={update} />
          <ChatInstallPanel
            fields={fields}
            onChange={update}
            agentUid={job.agent.uid}
          />
          <ChatCustomizationPanel fields={fields} onChange={update} />
        </TabsContent>

        <TabsContent value="capabilities" className={TAB_CONTENT_CLASS}>
          <ChatMessagesPanel fields={fields} onChange={update} />
          <ChatAppointmentsPanel
            fields={fields}
            onChange={update}
            connectedCalendars={job.connected_calendars ?? {}}
            calendlyEventTypes={
              (job.calendly_event_types ?? []) as CalendarEventType[]
            }
            calComEventTypes={
              (job.cal_com_event_types ?? []) as CalendarEventType[]
            }
          />
          {/* No Transfer calls: transfers are voice-only. */}
        </TabsContent>

        <TabsContent value="test" className={TAB_CONTENT_CLASS}>
          <ChatTestPanel agentUid={job.agent.uid} />
        </TabsContent>
      </Tabs>

      <SaveFooter processing={processing} onSave={save} />
    </>
  )
}

// ── Lead responder ───────────────────────────────────────────────────────────

function LeadResponderBody({
  job,
  onClose,
}: {
  job: ManagedJob
  onClose: () => void
}) {
  const [data, setData] = useState<LeadResponderSetupData>(() => ({
    ...DEFAULT_LEAD_RESPONDER_DATA,
    ...(job.agent.setup_data as Partial<LeadResponderSetupData>),
  }))
  const [processing, setProcessing] = useState(false)
  const update = (patch: Partial<LeadResponderSetupData>) =>
    setData((prev) => ({ ...prev, ...patch }))

  function save() {
    setProcessing(true)
    saveSetupData(job.agent.uid, data as unknown as Record<string, unknown>, {
      onFinish: () => setProcessing(false),
      onSuccess: onClose,
    })
  }

  return (
    <>
      <Tabs
        defaultValue="settings"
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        <TabsBar>
          <TabsList variant="line">
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
            <TabsTrigger value="test">Test</TabsTrigger>
          </TabsList>
        </TabsBar>

        <TabsContent value="settings" className={TAB_CONTENT_CLASS}>
          <SourcesFields
            data={data}
            onUpdate={update}
            leadIntake={job.lead_intake ?? undefined}
            crm={job.crm}
          />
          <CadenceFields data={data} onUpdate={update} showSpeed={false} />
          <CustomInstructionsField
            value={data.custom_instructions}
            placeholder={CUSTOM_INSTRUCTIONS_PLACEHOLDERS["lead-responder"]}
            onChange={(v) => update({ custom_instructions: v })}
          />
        </TabsContent>

        <TabsContent value="capabilities" className={TAB_CONTENT_CLASS}>
          {/* Book appointments + Transfer calls (destination number and
              weekly availability), each a toggle revealing its config. */}
          <HandoffFields
            data={data}
            onUpdate={update}
            calendars={
              {
                connectedCalendars: job.connected_calendars ?? {},
                calendlyEventTypes: job.calendly_event_types ?? [],
                calComEventTypes: job.cal_com_event_types ?? [],
              } as CalendarConnect
            }
          />
        </TabsContent>

        <TabsContent value="test" className={TAB_CONTENT_CLASS}>
          <LiveTestCall
            agentUid={job.agent.uid}
            // IMP-532: the test emulates an outbound follow-up — the agent
            // waits for the tester to answer before delivering its opener.
            idleDescription="You play the lead answering their phone — say “Hello?” and I'll take it from there."
            speakerLabel="Assistant"
            waitingMessage="Connected — answer like you've just picked up the phone."
          />
        </TabsContent>
      </Tabs>

      <SaveFooter processing={processing} onSave={save} />
    </>
  )
}

// ── Shell ────────────────────────────────────────────────────────────────────

function DrawerBody({
  job,
  view,
  onViewChange,
  onClose,
}: {
  job: ManagedJob
  view: "manage" | "forwarding"
  onViewChange: (view: "manage" | "forwarding") => void
  onClose: () => void
}) {
  const Icon = IDENTIFIER_ICON[job.slug]

  if (view === "forwarding") {
    // Forwarding is a pushed view inside the drawer rather than a dialog over
    // it — stacking a modal on a drawer puts two overlays on screen for one
    // task. It has nothing to save, so no footer.
    return (
      <>
        <DrawerHeader
          title={job.name}
          back={{ label: "Back", onClick: () => onViewChange("manage") }}
          className="border-b"
        />
        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          <ForwardingView receptionistNumber={job.phone_number ?? null} />
        </div>
      </>
    )
  }

  return (
    <>
      <DrawerHeader
        title={job.name}
        badge={job.paused ? <PausedBadge /> : undefined}
        subtitle={
          job.identifier ? (
            <span className="text-muted-foreground flex items-center gap-2 text-sm">
              <Icon className="size-3.5 shrink-0" />
              {job.identifier}
            </span>
          ) : undefined
        }
        action={<JobActions job={job} />}
      />
      {job.slug === "receptionist" && (
        <ReceptionistBody
          job={job}
          onClose={onClose}
          onForwarding={() => onViewChange("forwarding")}
        />
      )}
      {job.slug === "website-chat" && (
        <WebsiteChatBody job={job} onClose={onClose} />
      )}
      {job.slug === "lead-responder" && (
        <LeadResponderBody job={job} onClose={onClose} />
      )}
    </>
  )
}

export function ManageDrawer({
  job,
  open,
  initialView = "manage",
  onClose,
}: {
  job: ManagedJob | null
  open: boolean
  initialView?: "manage" | "forwarding"
  onClose: () => void
}) {
  const [view, setView] = useState<"manage" | "forwarding">(initialView)
  const [wasOpen, setWasOpen] = useState(open)

  // Reset the pushed view whenever the drawer reopens (adjusting state during
  // render, the React-recommended alternative to a setState effect).
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setView(initialView)
  }

  return (
    <DrawerSheet open={open} onOpenChange={(o) => !o && onClose()}>
      {job && (
        <DrawerBody
          key={job.agent.uid}
          job={job}
          view={view}
          onViewChange={setView}
          onClose={onClose}
        />
      )}
    </DrawerSheet>
  )
}
